/**
 * Modal form for creating a Domain inside a Site. Posts to
 * `/api/admin/sites/:siteId/domains`. The shared `createDomainSchema` is
 * the single source of validation; structural checks happen on the client
 * BEFORE the request, hostname normalisation happens on the server (per
 * `shared/src/schemas/domains.ts`).
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import type { CreateDomainInput } from "@ottopilot/shared";
import { createDomainSchema } from "@ottopilot/shared";
import { useCreateDomain } from "../../hooks/useDomains";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";

type Props = {
  open: boolean;
  onClose: () => void;
  siteId: string;
};

export function CreateDomainDialog({ open, onClose, siteId }: Props): ReactElement {
  const create = useCreateDomain(siteId);
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateDomainInput>({
    resolver: makeZodResolver(createDomainSchema),
    defaultValues: { hostname: "", isPrimary: false },
  });

  useEffect(() => {
    if (open) reset({ hostname: "", isPrimary: false });
  }, [open, reset]);

  const onSubmit = handleSubmit((values) => {
    create.mutate(values, {
      onSuccess: (domain) => {
        toast.showSuccess(`Added ${domain.hostname}.`);
        onClose();
      },
      onError: (err) => {
        toast.showError(err.message || "Could not add domain.");
      },
    });
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="create-domain-dialog"
    >
      <DialogTitle>New domain</DialogTitle>
      <Stack
        component="form"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <DialogContent>
          <Stack spacing={2}>
            {create.isError ? (
              <Alert severity="error" data-testid="create-domain-error-alert">
                {create.error.message}
              </Alert>
            ) : null}
            <TextField
              label="Hostname"
              fullWidth
              autoFocus
              placeholder="example.com"
              error={Boolean(errors.hostname)}
              helperText={
                errors.hostname?.message ??
                "DNS name without protocol — e.g. example.com or shop.example.com."
              }
              inputProps={{ "data-testid": "create-domain-hostname-input" }}
              {...register("hostname")}
            />
            <Controller
              name="isPrimary"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      slotProps={{
                        // The MUI v7 input slot's `InputHTMLAttributes` type
                        // doesn't list `data-testid`, but it is a valid
                        // pass-through DOM attribute and required for the E2E
                        // selector contract (`web/tests/e2e/E2E.md`).
                        input: { "data-testid": "create-domain-primary-switch" } as Record<
                          string,
                          string
                        >,
                      }}
                    />
                  }
                  label="Set as primary"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={create.isPending}
            data-testid="create-domain-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || create.isPending}
            data-testid="create-domain-submit-button"
          >
            {create.isPending ? "Adding..." : "Add domain"}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
